/**
 * Real-time list of the signed-in user's conversations.
 *
 * Subscribes to `users/{uid}/conversations` ordered by `updatedAt` (most recent
 * first) via `onSnapshot`, cleaning the listener up on unmount / uid change.
 */
import type { ConversationDoc } from '@agente/shared'
import { onSnapshot, orderBy, query } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { useAuth } from '@/lib/auth'
import { conversationsCol } from '@/lib/firestore'

export interface UseConversationsResult {
  conversations: ConversationDoc[]
  loading: boolean
  error: string | null
}

export function useConversations(): UseConversationsResult {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  const [conversations, setConversations] = useState<ConversationDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!uid) {
      setConversations([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)

    const q = query(conversationsCol(uid), orderBy('updatedAt', 'desc'))
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        // Filtro em memória das conversas excluídas (soft delete): o doc permanece
        // no Firestore como histórico, mas não aparece na lista do usuário. A
        // coleção por usuário é pequena — não muda a query nem exige índice.
        setConversations(
          snap.docs.map((d) => ({ ...d.data(), id: d.id })).filter((c) => c.deletedAt == null),
        )
        setLoading(false)
      },
      (err) => {
        setError(err.message)
        setLoading(false)
      },
    )
    return unsubscribe
  }, [uid])

  return { conversations, loading, error }
}
